import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './PredictiveFailureAnalysis.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';
import { fetchPredictiveFailure, fetchPrimarySteps } from './predictiveFailure/predictiveFailureApi';
import {
    buildPredictiveChartData,
    getPredictiveMetrics,
    predictiveChartOptions,
} from './predictiveFailure/predictiveFailureData';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const PredictiveFailure = () => {
    const [predictiveData, setPredictiveData] = useState([]);
    const [selectedPrimaryStep, setSelectedPrimaryStep] = useState('');
    const [primarySteps, setPrimarySteps] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [prediction, setPrediction] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const chartData = useMemo(() => buildPredictiveChartData(predictiveData), [predictiveData]);
    const metrics = useMemo(() => getPredictiveMetrics(predictiveData, prediction), [prediction, predictiveData]);
    const hasResults = predictiveData.length > 0 || prediction;

    useEffect(() => {
        const loadPrimarySteps = async () => {
            try {
                setPrimarySteps(await fetchPrimarySteps());
            } catch (fetchError) {
                console.error('Error fetching primary steps:', fetchError);
                setError('Failed to load primary steps. Please try again.');
            }
        };

        loadPrimarySteps();
    }, []);

    const fetchPredictiveData = async () => {
        if (!selectedPrimaryStep) {
            setError('Please select a primary step.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetchPredictiveFailure({
                selectedPrimaryStep,
                startDate,
                endDate,
            });

            setPredictiveData(response.historicalFailures || []);
            setPrediction(response.prediction || null);
        } catch (fetchError) {
            console.error('Error fetching predictive failure data:', fetchError);
            setPredictiveData([]);
            setPrediction(null);
            setError('Failed to load predictive failure data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedPrimaryStep('');
        setPredictiveData([]);
        setPrediction(null);
        setError(null);
    };

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>Failure forecasting</p>
                    <h2 className={styles.pageTitle}>Predictive Failure Analysis</h2>
                    <p className={styles.description}>
                        Select a primary step and date range to estimate upcoming failures from historical failure counts.
                    </p>
                </div>
            </header>

            {error && <div className={styles.errorText}>{error}</div>}

            <section className={styles.filterPanel}>
                <div className={styles.filterHeader}>
                    <div>
                        <h3>Filters</h3>
                        <p>The selected primary step drives the prediction model.</p>
                    </div>
                    <button onClick={clearFilters} className={styles.resetFilterButton}>
                        Clear
                    </button>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.dropdownContainer}>
                        <label htmlFor="primaryStep">Primary step</label>
                        <select
                            id="primaryStep"
                            value={selectedPrimaryStep}
                            onChange={(event) => setSelectedPrimaryStep(event.target.value)}
                            className={styles.dropdown}
                        >
                            <option value="">Select step</option>
                            {primarySteps.map((step) => (
                                <option key={step} value={step}>
                                    Step {step}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.datePickerContainer}>
                        <label>Start date</label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Any start date"
                            className={styles.datePicker}
                            isClearable
                        />
                    </div>

                    <div className={styles.datePickerContainer}>
                        <label>End date</label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Any end date"
                            className={styles.datePicker}
                            isClearable
                            minDate={startDate}
                        />
                    </div>
                </div>

                <div className={styles.filterFooter}>
                    <span className={styles.filterHint}>
                        {selectedPrimaryStep ? `Step ${selectedPrimaryStep} selected` : 'Choose a step to run the prediction'}
                    </span>
                    <button onClick={fetchPredictiveData} className={styles.applyFilterButton}>
                        {loading ? 'Running...' : 'Run prediction'}
                    </button>
                </div>
            </section>

            <section className={styles.metricsGrid}>
                {metrics.map((metric) => (
                    <div className={`${styles.metricCard} ${styles[metric.tone]}`} key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                    </div>
                ))}
            </section>

            <section className={styles.chartPanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>Historical failures</h3>
                        <p>The line shows past failure counts used by the prediction.</p>
                    </div>
                </div>

                <div className={styles.chartContainer}>
                    {loading ? (
                        <div className={styles.loadingScreen}>Loading predictive data...</div>
                    ) : predictiveData.length ? (
                        <Line data={chartData} options={predictiveChartOptions} />
                    ) : (
                        <div className={styles.emptyState}>
                            {selectedPrimaryStep ? 'No historical data found for these filters.' : 'Select a step and run the prediction.'}
                        </div>
                    )}
                </div>
            </section>

            {hasResults && (
                <section className={styles.predictionContainer}>
                    <h3>Prediction</h3>
                    <p>
                        Estimated failures: <strong>{prediction?.estimatedFailures ?? '-'}</strong>
                    </p>
                    <p>
                        Prediction date: <strong>{prediction?.predictionDate ?? '-'}</strong>
                    </p>
                </section>
            )}
        </div>
    );
};

export default PredictiveFailure;
